import { useState } from "react";
import {
  Alert,
  Button,
  Group,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";

import { api } from "@utils/api";


interface Props {
  token: string;
  identifier: string;
}

export const OrganizationRegistration = ({ token, identifier }: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [complete, setComplete] = useState(false);
  const [form, setForm] = useState({
    organization_name: "",
    practice_type: "clinic",
    contact_email: identifier.includes("@") ? identifier : "",
    contact_phone: identifier.includes("@") ? "" : identifier,
    address: "",
    city: "",
    country: "",
    admin_first_name: "",
    admin_last_name: "",
    password: "",
    confirm_password: "",
  });

  const set = (field: keyof typeof form, value: string) =>
    setForm(current => ({ ...current, [field]: value }));

  const valid =
    form.organization_name.trim().length >= 2 &&
    form.contact_email.includes("@") &&
    form.contact_phone.trim().length >= 5 &&
    form.admin_first_name.trim().length >= 2 &&
    form.admin_last_name.trim().length >= 2 &&
    form.password.length >= 8 &&
    form.password === form.confirm_password;

  const submit = async () => {
    setIsSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([field]) => field !== "confirm_password")
      );
      await api.post("account-management/organizations/onboarding/complete", {
        token,
        ...payload,
      });
      setComplete(true);
    } catch {
      notifications.show({
        color: "red",
        message: "Organization onboarding could not be completed.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (complete) {
    return (
      <Stack align="center" py="xl">
        <IconCheck size={48} color="var(--mantine-color-green-6)" />
        <Title order={3} ta="center">
          Organization account created
        </Title>
        <Text ta="center" c="dimmed">
          Your organization is now live. Sign in with the administrator email
          and password you just created.
        </Text>
        <Button component="a" href="/">
          Go to sign in
        </Button>
      </Stack>
    );
  }

  return (
    <Stack>
      <Alert color="violet" variant="light">
        Set up the organization and its first partner administrator.
      </Alert>
      <TextInput
        required
        label="Organization name"
        value={form.organization_name}
        onChange={event => set("organization_name", event.currentTarget.value)}
      />
      <Select
        label="Organization type"
        data={[
          { value: "clinic", label: "Clinic" },
          { value: "hospital", label: "Hospital" },
          { value: "laboratory", label: "Laboratory" },
          { value: "pharmacy", label: "Pharmacy" },
          { value: "group_practice", label: "Group practice" },
          { value: "other", label: "Other" },
        ]}
        value={form.practice_type}
        onChange={value => set("practice_type", value || "other")}
      />
      <Group grow align="start">
        <TextInput
          required
          type="email"
          label="Contact email"
          value={form.contact_email}
          onChange={event => set("contact_email", event.currentTarget.value)}
        />
        <TextInput
          required
          label="Contact phone"
          value={form.contact_phone}
          onChange={event => set("contact_phone", event.currentTarget.value)}
        />
      </Group>
      <TextInput
        label="Address"
        value={form.address}
        onChange={event => set("address", event.currentTarget.value)}
      />
      <Group grow align="start">
        <TextInput
          label="City"
          value={form.city}
          onChange={event => set("city", event.currentTarget.value)}
        />
        <TextInput
          label="Country"
          value={form.country}
          onChange={event => set("country", event.currentTarget.value)}
        />
      </Group>
      <Title order={5} mt="md">
        Partner administrator
      </Title>
      <Group grow align="start">
        <TextInput
          required
          label="First name"
          value={form.admin_first_name}
          onChange={event => set("admin_first_name", event.currentTarget.value)}
        />
        <TextInput
          required
          label="Last name"
          value={form.admin_last_name}
          onChange={event => set("admin_last_name", event.currentTarget.value)}
        />
      </Group>
      <PasswordInput
        required
        label="Password"
        description="At least 8 characters"
        value={form.password}
        onChange={event => set("password", event.currentTarget.value)}
      />
      <PasswordInput
        required
        label="Confirm password"
        error={
          form.confirm_password && form.password !== form.confirm_password
            ? "Passwords do not match"
            : undefined
        }
        value={form.confirm_password}
        onChange={event => set("confirm_password", event.currentTarget.value)}
      />
      <Button disabled={!valid} loading={isSaving} onClick={submit} mt="md">
        Create organization account
      </Button>
    </Stack>
  );
};
